// ui/easing.js: M5 Change #2.
//
// Canonical easing curves shared by CSS + canvas animation paths.
// Synthesis §4.4: card LANDINGS use a spring overshoot
// (cubic-bezier(0.34, 1.56, 0.64, 1), "easeOutBack"). Hovers and quick
// fades stay on standard easeOut.
//
// Why one module: M2 shipped placeholder easing inline at call sites
// (canvas/trick-area.js#easeOut). When the CSS surface (#11 score
// digits, #12 modal entry) adds a *second* spring curve and the
// canvas/Lua surfaces each carry their own copy, the curves drift.
// One module = one definition that the CSS, canvas, and Lua all agree
// on (Lua parity gated by spring_overshoot_test, plan M1).
//
// Reduced-motion: every spring helper has a *OrReduced sibling that
// collapses to instant (returns 1 immediately) so callers don't
// branch on the media query at every call site.

/**
 * Standard quadratic ease-out. Symmetric helper used for fades and the
 * trick sweep's translate (not a landing, the *landing* of the
 * winner pulse uses springOvershoot).
 *
 * @param {number} t  0..1
 * @returns {number}
 */
export function easeOut(t) {
  if (t <= 0) return 0;
  if (t >= 1) return 1;
  return 1 - (1 - t) * (1 - t);
}

/**
 * Cubic-bezier evaluator (Newton-Raphson). Solves for the parameter u
 * such that x(u) = t, then returns y(u). Mirrors the Lua-side solver
 * in client/src/lua/hello_card.lua so both runtimes draw the same
 * curve at the same time (M1 spring_overshoot_test parity gate).
 *
 * @param {number} t   0..1, the X coordinate to query
 * @param {number} x1  first control point X
 * @param {number} y1  first control point Y
 * @param {number} x2  second control point X
 * @param {number} y2  second control point Y
 * @returns {number}   y(u), NOT clamped (springs overshoot above 1)
 */
export function bezierY(t, x1, y1, x2, y2) {
  let u = t;  // initial guess
  for (let i = 0; i < 16; i++) {
    const mu = 1 - u;
    const xu = 3 * u * mu * mu * x1 + 3 * u * u * mu * x2 + u * u * u;
    const dxdu = 3 * mu * mu * x1 + 6 * u * mu * (x2 - x1) + 3 * u * u * (1 - x2);
    if (Math.abs(dxdu) < 1e-10) break;
    u = u - (xu - t) / dxdu;
    if (u < 0) u = 0;
    else if (u > 1) u = 1;
  }
  const mu = 1 - u;
  return 3 * u * mu * mu * y1 + 3 * u * u * mu * y2 + u * u * u;
}

/**
 * Spring landing curve, cubic-bezier(0.34, 1.56, 0.64, 1).
 * Peaks ~1.097 at t≈0.57, settles to 1.0 at t=1. Used by:
 *   • canvas card-landing scale pulse (#4)
 *   • CSS card transition for `.card.landed` (#2)
 *   • score-digit roller per-digit drop (#11)
 *
 * Cross-runtime parity: matches client/src/lua/hello_card.lua's
 * spring_overshoot() within 1e-9, both use the same Newton-Raphson
 * solver on the same control points.
 *
 * @param {number} t  0..1
 * @returns {number}  y value of the bezier at t (NOT clamped, overshoots above 1)
 */
export function springOvershoot(t) {
  if (t <= 0) return 0;
  if (t >= 1) return 1;
  return bezierY(t, 0.34, 1.56, 0.64, 1);
}

/**
 * springOvershoot collapsed under reduced motion.
 *
 * @param {number} t  0..1
 * @param {boolean} motionAllowed  false → returns 1 (instant landing)
 * @returns {number}
 */
export function springOvershootOrReduced(t, motionAllowed) {
  if (!motionAllowed) return t >= 1 ? 1 : 0;
  return springOvershoot(t);
}

/**
 * Linear interpolation between a and b.
 *
 * @param {number} a
 * @param {number} b
 * @param {number} t  0..1 (not clamped, callers may rely on overshoot)
 * @returns {number}
 */
export function lerp(a, b, t) {
  return a + (b - a) * t;
}

/**
 * Apply an easing curve to a [start, end] interval. Convenience for
 * `lerp(start, end, springOvershoot(t))`.
 *
 * @param {number} start
 * @param {number} end
 * @param {number} t        0..1
 * @param {(t:number)=>number} ease
 */
export function easeBetween(start, end, t, ease) {
  return lerp(start, end, ease(t));
}

// Canonical CSS strings, exported so #11 score-digit-roller and
// future CSS surfaces don't redeclare them.
export const CSS_SPRING_LAND = 'cubic-bezier(0.34, 1.56, 0.64, 1)';
export const CSS_EASE_OUT    = 'cubic-bezier(0.25, 1, 0.5, 1)';

// Plan §4.4 timing assignments.
export const TIMING = Object.freeze({
  CARD_SELECTED_MS:   200,
  CARD_DEALT_MS:      250,
  CARD_DEAL_STAGGER:  50,
  CARD_PLAY_MS:       100,
  CARD_SNAP_MS:       80,
  TRICK_SWEEP_MS:     130,
  HAND_REARRANGE_MS:  150,
  END_OF_HAND_MS:     300,
  SCORE_DIGIT_MS:     50,
  REDUCED_FADE_MS:    150,
});
